import {
    Injectable, inject
} from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
    BulkResponse,
    BulkUtil,
    CreateRequest,
    DeleteRequest,
    DeleteResponse,
    GetResponse,
    RequestUtil,
    Role,
    RoleSearchFilterOptions,
    SearchResponse,
    UpdateRequest
} from "@shopliftr/common-js/shared";
import {
    ISearchSort,
    ModelAction
} from "@shopliftr/common-js/data";
import { IFullAccessInternalApiClientService } from "../../../../interfaces/internal-api-client";
import { InternalApiRequestService } from "../../request/internal-api/internal-api-request.service";
import { ListUtil } from "../../../../list/util/list-util";

/**
 * API Client Service for Role entities.
 *
 * Implements full CRUD operations for Role entities via the Internal API v2.
 * Delegates HTTP communication to InternalApiRequestService while encapsulating
 * Role-specific request/response handling.
 *
 * Replaces the legacy {@link RoleService} which is now deprecated.
 *
 * @example
 * ```typescript
 * private readonly _roleApiClientService = inject(RoleApiClientService);
 *
 * this._roleApiClientService.get$('role-123').subscribe(role => console.log(role));
 * this._roleApiClientService.bulkGet$(['r1', 'r2']).subscribe(r => console.log(r));
 * this._roleApiClientService.getAll$(new RoleSearchFilterOptions()).subscribe(roles => console.log(roles));
 * this._roleApiClientService.create$(role).subscribe(created => console.log(created));
 * this._roleApiClientService.delete$(role.id, role.esVersion).subscribe(() => console.log('deleted'));
 * ```
 *
 * @pattern IFullAccessInternalApiClientService
 * @providedIn root
 * @implements {IFullAccessInternalApiClientService<Role>}
 */
@Injectable({ providedIn: "root" })
export class RoleApiClientService implements IFullAccessInternalApiClientService<Role> {

    readonly basePath = "role";

    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieve multiple Role entities by their IDs in a single bulk request.
     *
     * @param ids Array of unique identifiers to retrieve
     * @returns Observable emitting a BulkResponse containing the requested entities
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<Role>(id, Role.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Retrieve a single Role by its unique identifier.
     *
     * @param id The unique identifier of the entity to retrieve
     * @returns Observable emitting the Role entity
     */
    get$(id: string): Observable<Role> {
        const request = RequestUtil.createGetRequest<void>(id, Role.modelName, ModelAction.ClientGet);
        return this._internalApiRequestService.get$<Role>(request, this.basePath).pipe(
            map((response: GetResponse<Role>) => response.payload)
        );
    }

    /**
     * Retrieve all Role entities matching the provided filter criteria.
     *
     * @param filterOptions The search criteria to apply
     * @param sort Array of sort criteria to apply (optional)
     * @returns Observable emitting an array of all matching entities
     */
    getAll$(filterOptions: RoleSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<Role>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Role.modelName,
            undefined,
            undefined,
            filterOptions,
            sort
        );
        return this._internalApiRequestService.getAll$(request, this.basePath);
    }

    /**
     * Search for Role entities with pagination support.
     *
     * @param filterOptions The search criteria to apply
     * @param page Zero-based page index for pagination (optional)
     * @param pageSize Number of items per page (optional)
     * @param sort Array of sort criteria to apply (optional)
     * @returns Observable emitting search results with pagination metadata
     */
    search$(
        filterOptions: RoleSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<Role>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Role.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );
        return this._internalApiRequestService.search$(request, this.basePath);
    }

    /**
     * Create a new Role entity.
     *
     * @param fields The Role model containing the field values for the new entity
     * @returns Observable emitting the created Role entity
     */
    create$(fields: Role): Observable<Role> {
        const createRequest = RequestUtil.createCreateRequest<Role, void>(
            ModelAction.ClientCreate,
            fields.cloneForAction(ModelAction.ClientCreate)
        );
        return this._internalApiRequestService.create$(createRequest, this.basePath)
            .pipe(map((response) => response.payload));
    }

    /**
     * Create multiple Role entities in a single bulk request.
     *
     * @param items Array of create requests for roles to create
     * @returns Observable emitting a BulkResponse with results of each create operation
     */
    bulkCreate$(items: Array<CreateRequest<Role, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Update a Role entity by ID and version.
     *
     * @param id Unique identifier of the role to update
     * @param version Version for optimistic concurrency control
     * @param fields Role model containing the updated field values
     * @param removeFields Optional array of field names to remove
     * @returns Observable emitting the updated Role entity
     */
    update$(id: string, version: number, fields: Role, removeFields?: Array<string>): Observable<Role> {
        const updateFields = fields.cloneForAction(ModelAction.ClientUpdate);
        const updateRequest: UpdateRequest<Role> = RequestUtil.createUpdateRequest(
            id, ModelAction.ClientUpdate, updateFields, version, removeFields
        );
        return this._internalApiRequestService.update$(updateRequest, this.basePath)
            .pipe(map((response) => response.payload));
    }

    /**
     * Update a Role entity by diffing original and updated models.
     *
     * @param original The original Role model
     * @param updated The Role model with changes applied
     * @returns Observable emitting the updated Role entity
     */
    updateWithChanges$(original: Role, updated: Role): Observable<Role> {
        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath);
    }

    /**
     * Update multiple Role entities in a single bulk request.
     *
     * @param requests Array of update requests
     * @returns Observable emitting a BulkResponse with results of each update operation
     */
    bulkUpdate$(requests: Array<UpdateRequest<unknown, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            requests.map((request) => BulkUtil.createRequestItem(request))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Delete a Role entity by ID and version.
     *
     * @param id Unique identifier of the role to delete
     * @param version Version for optimistic concurrency control
     * @returns Observable emitting the DeleteResponse confirming deletion
     */
    delete$(id: string, version: number): Observable<DeleteResponse> {
        const deleteRequest = RequestUtil.createDeleteRequest<Role>(
            id, Role.modelName, ModelAction.ClientDelete, version
        );
        return this._internalApiRequestService.delete$(deleteRequest, this.basePath);
    }

    /**
     * Delete multiple Role entities in a single bulk request.
     *
     * @param items Array of delete requests
     * @returns Observable emitting a BulkResponse with results of each delete operation
     */
    bulkDelete$(items: Array<DeleteRequest<unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }
}
